import React, { useState, useRef, useEffect } from 'react';

export interface ActionMenuItem {
  icon: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  disabledReason?: string;
  separator?: boolean;
}

interface ActionMenuProps {
  items: ActionMenuItem[];
  triggerTitle?: string;
}

export const ActionMenu: React.FC<ActionMenuProps> = ({ items, triggerTitle = 'Más acciones' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const menuWidth = 224; // w-56
      const approxMenuHeight = items.length * 40 + 16;
      const spaceBelow = window.innerHeight - rect.bottom;
      const top =
        spaceBelow < approxMenuHeight && rect.top > approxMenuHeight
          ? rect.top - approxMenuHeight
          : rect.bottom + 4;
      const left = Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8);
      setPos({ top, left });
    }
    setIsOpen((prev) => !prev);
  };

  return (
    <div className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggle}
        title={triggerTitle}
        className="inline-flex items-center justify-center w-8 h-8 text-base rounded-md bg-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400"
      >
        •••
      </button>

      {isOpen && (
        <div
          ref={menuRef}
          className="fixed z-50 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-600"
          style={{ top: pos.top, left: pos.left }}
        >
          <div className="py-1">
            {items.map((item, idx) => (
              <React.Fragment key={idx}>
                {item.separator && (
                  <div className="border-t border-gray-200 dark:border-gray-600 my-1" />
                )}
                <button
                  type="button"
                  onClick={() => {
                    if (!item.disabled) {
                      item.onClick();
                      setIsOpen(false);
                    }
                  }}
                  disabled={item.disabled}
                  title={item.disabled && item.disabledReason ? item.disabledReason : item.label}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2.5"
                >
                  <span className="text-base leading-none flex-shrink-0">{item.icon}</span>
                  <span className="flex-1">{item.label}</span>
                  {item.disabled && item.disabledReason && (
                    <span className="text-xs text-gray-400 dark:text-gray-500 truncate max-w-[80px] flex-shrink-0">
                      {item.disabledReason}
                    </span>
                  )}
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
